import {
	JourneyExploreResult,
	JourneyGroup,
	JourneyInteraction,
	JourneyMeta,
	JourneySimplify,
	JourneyTransition,
	JourneyFilterState,
} from '../../../models/journey-explorer/journey-explorer';
import { EMPTY_SIMPLIFY, MAX_GROUPS, MAX_GROUP_LABEL } from './journey-simplify';
import { visibleInteractions } from './journey-map-layout';
import { createFilterState } from './journey-explorer-utils';

const STAGE_THRESHOLD = 20;
const MAX_STAGE_MEMBERS = 50;
const SMALL_MAP = 10;
const COVERAGE = 0.8;
const MAX_FUNNEL_STEPS = 30;
const REPEAT_RATIO = 1.3;
const SLOW_FACTOR = 2;
const SLOW_MIN_SECS = 60;
const PLACED_ORDER =
	/(place|placed|complete|completed|confirm|confirmed|success).*(order|purchase)|(order|purchase).*(place|placed|complete|completed|confirm|confirmed|success)/i;
const ANY_ORDER = /order|purchase|convert|subscribe/i;

export const stageGroups = (meta: JourneyMeta): JourneyGroup[] => {
	const names = new Set(meta.interaction_types.flatMap((type) => type.interactions.map((item) => item.name)));
	return meta.interaction_types
		.filter(
			(type) =>
				type.interactions.length > 1 &&
				type.interactions.length <= MAX_STAGE_MEMBERS &&
				type.label.trim() === type.label &&
				type.label.length > 0 &&
				type.label.length <= MAX_GROUP_LABEL &&
				!names.has(type.label)
		)
		.slice(0, MAX_GROUPS)
		.map((type) => ({ label: type.label, names: type.interactions.map((item) => item.name) }));
};

export const opensOnStages = (meta: JourneyMeta): boolean =>
	meta.interaction_types.reduce((total, type) => total + type.interactions.length, 0) > STAGE_THRESHOLD &&
	stageGroups(meta).length > 1;

export const allInteractionNames = (meta: JourneyMeta): string[] =>
	meta.interaction_types.flatMap((type) => type.interactions.map((interaction) => interaction.name));

// A stored preference of null means the user chose no goal; undefined means pick one for them.
export const pickGoal = (meta: JourneyMeta, preferred?: string | null): string | null => {
	const names = allInteractionNames(meta);
	if (preferred === null) {
		return null;
	}
	return preferred && names.includes(preferred) ? preferred : guessGoal(names);
};

export const defaultFilterState = (meta: JourneyMeta, preferredGoal?: string | null): JourneyFilterState => {
	const goal = pickGoal(meta, preferredGoal);
	const state: JourneyFilterState = {
		...createFilterState(meta),
		goal: goal ? { interaction: goal, outcome: null } : null,
	};
	return opensOnStages(meta) ? { ...state, simplify: withStages(EMPTY_SIMPLIFY, meta) } : state;
};

export const showingStages = (simplify: JourneySimplify, meta: JourneyMeta): boolean => {
	const stages = stageGroups(meta);
	return stages.length > 0 && stages.every((stage) => simplify.groups.some((group) => group.label === stage.label));
};

export const withStages = (simplify: JourneySimplify, meta: JourneyMeta): JourneySimplify => {
	const stages = stageGroups(meta);
	const members = new Set(stages.flatMap((stage) => stage.names));
	const labels = new Set(stages.map((stage) => stage.label));
	const kept = simplify.groups.filter(
		(group) => !labels.has(group.label) && group.names.every((name) => !members.has(name))
	);
	return {
		hidden: simplify.hidden,
		groups: [...kept, ...stages].slice(0, MAX_GROUPS).sort((a, b) => a.label.localeCompare(b.label)),
	};
};

export const withoutStages = (simplify: JourneySimplify, meta: JourneyMeta): JourneySimplify => {
	const labels = new Set(stageGroups(meta).map((stage) => stage.label));
	return { ...simplify, groups: simplify.groups.filter((group) => !labels.has(group.label)) };
};

const coverageCount = (values: number[]): number => {
	const sorted = [...values].sort((a, b) => b - a);
	const total = sorted.reduce((sum, value) => sum + value, 0);
	let running = 0;
	const index = sorted.findIndex((value) => {
		running += value;
		return running >= total * COVERAGE;
	});
	return index < 0 ? sorted.length : index + 1;
};

// The smallest slider settings whose boxes and lines still cover most of the customers' steps.
export const suggestedPercents = (result: JourneyExploreResult): { interactions: number; transitions: number } => {
	const count = result.interactions.length;
	const interactions =
		count <= SMALL_MAP
			? 100
			: Math.ceil((coverageCount(result.interactions.map((item) => item.customers)) / count) * 100);
	const visible = new Set(visibleInteractions(result.interactions, interactions).map((item) => item.name));
	const lines = result.transitions.filter(
		(item) => item.from !== item.to && visible.has(item.from) && visible.has(item.to)
	);
	const transitions = lines.length
		? Math.ceil((coverageCount(lines.map((item) => item.customers)) / lines.length) * 100)
		: 100;
	return { interactions, transitions };
};

export const guessGoal = (names: string[]): string | null =>
	names.find((name) => PLACED_ORDER.test(name)) ?? names.find((name) => ANY_ORDER.test(name)) ?? null;

// Follows the busiest unvisited transition from the step most customers enter at.
export const mainRoute = (
	interactions: JourneyInteraction[],
	transitions: JourneyTransition[],
	goal: string | null
): string[] => {
	if (interactions.length === 0) {
		return [];
	}
	const incoming = new Map<string, number>();
	const outgoing = new Map<string, JourneyTransition[]>();
	transitions
		.filter((item) => item.from !== item.to)
		.forEach((item) => {
			incoming.set(item.to, (incoming.get(item.to) ?? 0) + item.customers);
			outgoing.set(item.from, [...(outgoing.get(item.from) ?? []), item]);
		});
	outgoing.forEach((items) => items.sort((a, b) => b.customers - a.customers));
	const entries = (item: JourneyInteraction) => item.customers - (incoming.get(item.name) ?? 0);
	const start = [...interactions].sort((a, b) => entries(b) - entries(a) || b.customers - a.customers)[0].name;

	const route = [start];
	const visited = new Set(route);
	while (route.length < MAX_FUNNEL_STEPS && route[route.length - 1] !== goal) {
		const next = (outgoing.get(route[route.length - 1]) ?? []).find((item) => !visited.has(item.to));
		if (!next) {
			break;
		}
		route.push(next.to);
		visited.add(next.to);
	}
	return route;
};

export interface FunnelStep {
	name: string;
	reached: number;
	continued: number | null;
	continuedPct: number | null;
	stopped: number;
	stoppedPct: number;
	medianSecs: number | null;
	repeatRatio: number;
	repeated: boolean;
	slow: boolean;
}

const share = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);

const repeatRatio = (item: JourneyInteraction) => (item.customers > 0 ? item.interactions / item.customers : 1);

const median = (values: number[]): number | null => {
	if (values.length === 0) {
		return null;
	}
	const sorted = [...values].sort((a, b) => a - b);
	const middle = Math.floor(sorted.length / 2);
	return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const slowLimit = (interactions: JourneyInteraction[]): number => {
	const typical = median(
		interactions.flatMap((item) => (item.median_secs_to_next === null ? [] : [item.median_secs_to_next]))
	);
	return Math.max(SLOW_MIN_SECS, (typical ?? 0) * SLOW_FACTOR);
};

export const buildFunnel = (result: JourneyExploreResult, route: string[]): FunnelStep[] => {
	const stats = new Map(result.interactions.map((item) => [item.name, item]));
	const moves = new Map(result.transitions.map((item) => [`${item.from}\u0000${item.to}`, item]));
	const limit = slowLimit(result.interactions);
	return route.flatMap((name, index): FunnelStep[] => {
		const stat = stats.get(name);
		if (!stat) {
			return [];
		}
		const next = route[index + 1];
		const continued = next ? moves.get(`${name}\u0000${next}`)?.customers ?? 0 : null;
		const ratio = repeatRatio(stat);
		return [
			{
				name,
				reached: stat.customers,
				continued,
				continuedPct: continued === null ? null : share(continued, stat.customers),
				stopped: stat.exits,
				stoppedPct: share(stat.exits, stat.customers),
				medianSecs: stat.median_secs_to_next,
				repeatRatio: ratio,
				repeated: ratio >= REPEAT_RATIO,
				slow: stat.median_secs_to_next !== null && stat.median_secs_to_next > limit,
			},
		];
	});
};

export interface DropOff {
	name: string;
	stopped: number;
	reached: number;
	stoppedPct: number;
}

export const dropOffs = (interactions: JourneyInteraction[], goal: string | null, limit = 8): DropOff[] =>
	interactions
		.filter((item) => item.name !== goal && item.exits > 0)
		.sort((a, b) => b.exits - a.exits || a.name.localeCompare(b.name))
		.slice(0, limit)
		.map((item) => ({
			name: item.name,
			stopped: item.exits,
			reached: item.customers,
			stoppedPct: share(item.exits, item.customers),
		}));

export interface Friction {
	name: string;
	reason: 'repeated' | 'slow';
	repeatRatio: number;
	medianSecs: number | null;
	customers: number;
}

export const frictionSteps = (interactions: JourneyInteraction[], limit = 6): Friction[] => {
	const slow = slowLimit(interactions);
	return interactions
		.flatMap((item): Friction[] => {
			const ratio = repeatRatio(item);
			const base = {
				name: item.name,
				repeatRatio: ratio,
				medianSecs: item.median_secs_to_next,
				customers: item.customers,
			};
			if (ratio >= REPEAT_RATIO) {
				return [{ ...base, reason: 'repeated' }];
			}
			if (item.median_secs_to_next !== null && item.median_secs_to_next > slow) {
				return [{ ...base, reason: 'slow' }];
			}
			return [];
		})
		.sort((a, b) => b.customers - a.customers)
		.slice(0, limit);
};
